/**
 * The sound bank: every `SFX` patch rendered to `AudioBuffer`s (07 §2). Rendering a couple of
 * hundred buffers costs a second or two of CPU, so it is spread out: `warm` renders in small
 * time slices while the menus are up, and anything asked for before its turn is rendered on
 * the spot (one late frame, once, instead of a missing sound).
 */

import { SFX, SFX_IDS, type SfxId } from '../sfx';
import { hashName } from '../synth/rng';
import { renderPatch } from '../synth/render';

export class Bank {
  private readonly buffers = new Map<SfxId, Array<AudioBuffer | undefined>>();
  /** Round-robin cursor per sound, so consecutive plays use different variants. */
  private readonly cursor = new Map<SfxId, number>();
  private warmId = 0;
  private warmVariant = 0;

  constructor(private readonly ctx: BaseAudioContext) {
    for (const id of SFX_IDS) {
      this.buffers.set(id, new Array<AudioBuffer | undefined>(SFX[id].variants).fill(undefined));
      this.cursor.set(id, 0);
    }
  }

  private render(id: SfxId, variant: number): AudioBuffer {
    const samples = renderPatch(SFX[id].patch, this.ctx.sampleRate, hashName(id) + variant * 7919);
    const buffer = this.ctx.createBuffer(1, samples.length, this.ctx.sampleRate);
    buffer.copyToChannel(samples, 0);
    return buffer;
  }

  get(id: SfxId, variant: number): AudioBuffer {
    const list = this.buffers.get(id);
    const existing = list?.[variant];
    if (existing) return existing;
    const made = this.render(id, variant);
    if (list) list[variant] = made;
    return made;
  }

  /** The next variant of a sound, cycling. */
  next(id: SfxId): AudioBuffer {
    const variants = SFX[id].variants;
    const at = this.cursor.get(id) ?? 0;
    this.cursor.set(id, (at + 1) % variants);
    return this.get(id, at % variants);
  }

  /** Render not-yet-rendered buffers for up to `budgetMs`. Returns true once the whole bank is in memory. */
  warm(budgetMs: number): boolean {
    const started = performance.now();
    while (this.warmId < SFX_IDS.length) {
      const id = SFX_IDS[this.warmId];
      if (!id || this.warmVariant >= SFX[id].variants) {
        this.warmId++;
        this.warmVariant = 0;
        continue;
      }
      this.get(id, this.warmVariant++);
      if (performance.now() - started >= budgetMs) return false;
    }
    return true;
  }
}
